/**
 * hooks/useLayoutEditor.js — state container for the card editor.
 *
 * Chunk 3 (Apr 24 2026).
 *
 * Holds:
 *   - current layout (mutable in-memory working copy)
 *   - selected element id (or null)
 *   - undo/redo stacks (50-deep each)
 *   - dirty flag (set on any mutation, cleared on save)
 *
 * Exposes:
 *   setInitialLayout(layout)  — load a layout (from server or preset)
 *                               clears history, dirty=false
 *   selectedElement           — the element currently selected, or null
 *   select(id)                — select by id, null deselects
 *   updateElement(id, patch)  — merge partial into element, push history
 *   moveElement(id, {x,y})    — shorthand for drag
 *   resizeElement(id, {x,y,w,h}) — shorthand for resize
 *   deleteElement(id)         — remove (no history push for undeletable types)
 *   undo() / redo()
 *   canUndo / canRedo
 *   dirty                     — any changes since last setInitialLayout or markClean
 *   markClean()               — called after successful save
 *
 * History is layout-snapshot-based, not patch-based. Simpler to reason
 * about at the cost of a bit of memory (50 × ~3KB = 150KB ceiling).
 */

import { useCallback, useMemo, useState } from 'react';

const HISTORY_LIMIT = 50;

export function useLayoutEditor() {
  const [layout, setLayout] = useState(null);
  const [history, setHistory] = useState([]);         // past states
  const [future, setFuture] = useState([]);           // redo stack
  const [selectedId, setSelectedId] = useState(null);
  const [dirty, setDirty] = useState(false);

  /**
   * Load a fresh layout. Clears history and dirty flag. Used both on
   * modal-open (load saved or preset) and on reset-to-preset action.
   */
  const setInitialLayout = useCallback((initial) => {
    setLayout(clone(initial));
    setHistory([]);
    setFuture([]);
    setSelectedId(null);
    setDirty(false);
  }, []);

  const pushHistory = useCallback((prevLayout) => {
    setHistory((h) => {
      const next = [...h, prevLayout];
      if (next.length > HISTORY_LIMIT) next.shift();
      return next;
    });
    setFuture([]); // any new action drops redo
  }, []);

  const applyMutation = useCallback((mutator) => {
    setLayout((current) => {
      if (!current) return current;
      pushHistory(clone(current));
      setDirty(true);
      return mutator(clone(current));
    });
  }, [pushHistory]);

  const select = useCallback((id) => setSelectedId(id), []);

  const updateElement = useCallback((id, patch) => {
    applyMutation((draft) => {
      const idx = draft.elements.findIndex((e) => e.id === id);
      if (idx === -1) return draft;
      const prev = draft.elements[idx];
      draft.elements[idx] = {
        ...prev,
        ...patch,
        props: patch.props ? { ...prev.props, ...patch.props } : prev.props,
      };
      return draft;
    });
  }, [applyMutation]);

  const moveElement = useCallback((id, { x, y }) => {
    updateElement(id, { x, y });
  }, [updateElement]);

  const resizeElement = useCallback((id, { x, y, w, h }) => {
    updateElement(id, { x, y, w, h });
  }, [updateElement]);

  const deleteElement = useCallback((id) => {
    applyMutation((draft) => {
      draft.elements = draft.elements.filter((e) => e.id !== id);
      return draft;
    });
    setSelectedId((cur) => (cur === id ? null : cur));
  }, [applyMutation]);

  const undo = useCallback(() => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setLayout((current) => {
        if (current) setFuture((f) => [...f, clone(current)]);
        setDirty(true);
        return clone(prev);
      });
      return h.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (f.length === 0) return f;
      const next = f[f.length - 1];
      setLayout((current) => {
        if (current) setHistory((h) => [...h, clone(current)]);
        setDirty(true);
        return clone(next);
      });
      return f.slice(0, -1);
    });
  }, []);

  const markClean = useCallback(() => setDirty(false), []);

  const selectedElement = useMemo(() => {
    if (!layout || !selectedId) return null;
    return layout.elements.find((e) => e.id === selectedId) || null;
  }, [layout, selectedId]);

  return {
    layout,
    setInitialLayout,
    selectedId,
    selectedElement,
    select,
    updateElement,
    moveElement,
    resizeElement,
    deleteElement,
    undo,
    redo,
    canUndo: history.length > 0,
    canRedo: future.length > 0,
    dirty,
    markClean,
  };
}

/**
 * Deep clone via JSON. Layouts are pure JSON so this is safe and
 * fast enough for the volumes we see (~30 elements, ~3KB).
 */
function clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}
